import { type Asset } from 'types/asset.types';
import { type DTC } from 'types/dtc.types';
import { type GeoTrip } from 'types/geo.types';
import { type RuleNotifications } from 'types/notification.types';
import { type TripData, type TripDetails } from 'types/production.types';
import { COLORS, NOTIFICATION_LEVEL } from 'utils/enums';
import { coordsMap } from 'utils/helpers/general';
import { type preferredTimeDisplayModeTypes } from 'utils/helpers/locale-date-utils';
import { formatTripData } from 'views/Production/Hauler/component/utils';
import type { NavigateFunction } from 'react-router-dom';
import { cloneDeep } from 'lodash';

// todo update type defs when we have them
export const addLiveDataToAssets = (
  // list of assets with device data
  associatedAssets: Asset[],
  liveTagData: Record<string, Record<string, any>>,
  notificationData?: RuleNotifications[],
  latestPaths?: GeoTrip[],
  dtc?: DTC[]
): Asset[] => {
  return associatedAssets.map((asset): Asset => {
    if (asset?.device?.deviceId) {
      const liveTagDataForAsset = liveTagData[asset.device.deviceId];
      const notificationForAsset = notificationData
        ? notificationData.filter(
            (notification: any) =>
              notification.deviceId === asset.device.deviceId &&
              notification.isActive === true
          )
        : [];
      const assetCopied = cloneDeep(asset);
      assetCopied.device.daysBehind = liveTagDataForAsset?.daysBehind;
      assetCopied.device.ingestDate = liveTagDataForAsset?.ingestDate;
      assetCopied.device.last_location =
        liveTagDataForAsset?.last_location || assetCopied.device.last_location;
      assetCopied.device.last_location_ts =
        liveTagDataForAsset?.last_location_ts ||
        assetCopied.device.last_location_ts;
      assetCopied.device.last_location_duration =
        liveTagDataForAsset?.last_location_duration ||
        assetCopied.device.last_location_duration;
      const daysBehind = assetCopied.device.daysBehind;
      let latestGeoPaths;
      let dtcData;

      if (latestPaths) {
        latestGeoPaths = latestPaths?.find((path: GeoTrip) => {
          return asset.device.deviceId === path.device;
        });
      }
      if (dtc) {
        dtcData = Object.entries(dtc)
          .filter(([key, value]) => value.device === asset.device.deviceId)
          .reduce((acc: any, [key, value]) => {
            acc[key] = value;
            return acc;
          }, {});
      }

      // Update deviceStatus based on daysBehind
      if (daysBehind >= 0 && daysBehind < 0.042) {
        assetCopied.device.deviceStatus = 'Online';
      } else if (daysBehind >= 0.042 && daysBehind < 1) {
        assetCopied.device.deviceStatus = 'Recently Online';
      } else if (daysBehind >= 1 && daysBehind < 30) {
        assetCopied.device.deviceStatus = 'Offline';
      } else {
        // Handle any other cases here, e.g., set to 'Unknown'
        assetCopied.device.deviceStatus = 'Offline > 30D';
      }
      return {
        ...assetCopied,
        liveTagData: liveTagDataForAsset,
        notifications: notificationForAsset,
        latestGeoPaths,
        dtcData,
      };
    } else {
      return {
        ...asset,
      };
    }
  });
};

export const notificationLevel = (
  notifications?: RuleNotifications[]
): { color: string; warning: string; level: number; message: string } => {
  let color, warning, level, message;
  if (notifications && notifications?.length <= 0) {
    color = COLORS.green; // Greeen  #749A4A
    warning = 'No Alerts';
    level = 0;
    message = 'Ideal Performance';
  } else if (
    notifications?.some(
      (el: any) => el.rule.notificationLevel === NOTIFICATION_LEVEL[2].id
    )
  ) {
    color = COLORS.red; // Red #A03434
    warning = NOTIFICATION_LEVEL[2].display;
    level = Number(NOTIFICATION_LEVEL[2].id);
    message = 'Attention Required';
  } else if (
    notifications?.some(
      (el: any) => el.rule.notificationLevel === NOTIFICATION_LEVEL[1].id
    )
  ) {
    color = COLORS.yellow; // Yellow #F5C274
    warning = NOTIFICATION_LEVEL[1].display;
    level = Number(NOTIFICATION_LEVEL[1].id);
    message = 'Caution Advised';
  } else if (
    notifications?.some(
      (el: any) => el.rule.notificationLevel === NOTIFICATION_LEVEL[0].id
    )
  ) {
    color = COLORS.grey; // GREY #77859F
    warning = NOTIFICATION_LEVEL[0].display;
    level = Number(NOTIFICATION_LEVEL[0].id);
    message = 'Caution Advised';
  } else {
    color = COLORS.green; // Greeen  #749A4A
    warning = 'No Alerts';
    level = 0;
    message = 'Ideal Performance';
  }
  return { color, warning, level, message };
};

export function transformText(input: string) {
  // Split the string into words
  let words = input.split(/[\s_]+/);

  // Filter out the word 'at'
  words = words.filter((word) => word.toLowerCase() !== 'at');

  // Capitalize the first letter of each remaining word
  words = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1));

  // Join the words back together with a space
  return words.join(' ');
}

export function calculatePercentageChange(
  firstNumber: number,
  secondNumber: number
) {
  let percentageChange: number;

  if (firstNumber === 0) {
    if (secondNumber === 0) {
      percentageChange = 0;
    } else {
      percentageChange = secondNumber > 0 ? Infinity : -Infinity;
    }
  } else {
    percentageChange = ((secondNumber - firstNumber) / firstNumber) * 100;
  }

  return {
    percentageChange: isFinite(percentageChange)
      ? percentageChange.toFixed(2)
      : percentageChange.toString(),
    isIncrement: secondNumber > firstNumber,
  };
}

export const assetTypeMappings: Record<string, string> = {
  haul_truck: 'Haul Truck',
  light_truck: 'Haul Truck',
  heavy_truck: 'Haul Truck',
  loader: 'Loader',
  dozer: 'Others',
  grader: 'Others',
  excavator: 'Others',
  drill: 'Others',
  bolter: 'Others',
  miscellaneous: 'Others',
};

// Translation-key lookup for displaying assetTypeMappings values — use at
// render sites only. assetTypeMappings itself must stay English-keyed since
// its values are also used for filtering/comparison logic across the app.
export const getAssetTypeLabelKey = (mappedType: string): string => {
  switch (mappedType) {
    case 'Haul Truck':
      return 'asset_type_haul_truck';
    case 'Loader':
      return 'asset_type_loader';
    default:
      return 'asset_type_others';
  }
};

export const getAssetType = (assetName: string) => {
  switch (assetName) {
    case 'Haul Truck':
      return 'haul_truck';
    case 'Loader':
      return 'loader';
    default:
      return 'others';
  }
};

export function findHighestAndValuesForKey(data: any, key: any) {
  const assetValues = data.map((obj: any) => {
    return obj[key] ?? 0;
  });

  const sumOfHighestNumbers = assetValues.reduce(
    (acc: number, cur: number) => acc + cur,
    0
  );

  const percentages = assetValues.map((highest: any) => {
    return (highest / sumOfHighestNumbers) * 100;
  });

  const mergedArr = data.map((item: any, index: any) => {
    return {
      ...item,
      'Cumulative Total': percentages[index].toFixed(2) ?? 0,
    };
  });
  return mergedArr;
}

export const assetHasDTC = (asset: Asset): boolean => {
  const dtcData = asset?.dtcData;
  if (!dtcData || typeof dtcData !== 'object') return false;

  // checking if any of the dtcData has is_active true return true else retrun false
  return Object.values(dtcData).some((dtc: any) => dtc?.is_active === true);
};
export const assetHasSuggestions = (
  asset: Asset,
  suggestions: TripData[]
): number => {
  const now = new Date();
  const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const details = suggestions
    .flatMap((trip) => trip.outlier_trips_based_on_time)
    .filter((item): item is TripDetails => item !== undefined)
    .filter((item) => {
      // Parse UTC date and adjust it to local time zone
      const [datePart, timePart] = item.date.split(' '); // Extract "YYYY-MM-DD" and "HH:MM:SS.sss"
      const utcDate = new Date(`${datePart}T${timePart}Z`); // Convert to proper UTC Date

      // Convert UTC Date to local time
      const itemDateLocal = new Date(
        utcDate.getTime() + new Date().getTimezoneOffset() * 60 * 1000 * -1
      );
      return (
        item.device === asset.device.assetBumperNumber &&
        itemDateLocal >= last24Hours &&
        itemDateLocal <= now
      );
    });

  return details.length;
};

export const isAssetIgnition = (
  latestSensorDetails: any,
  asset: Asset
): boolean => {
  const sensorData = latestSensorDetails?.[`${asset?.device?.deviceId}`];

  // Priority: the live-gps-sse stream. When the payload carries
  // sb_ignition_status, trust it and gate freshness on the stream's own
  // latest_ts (≤ 1 hour) instead of the poll-based device fields.
  if (sensorData?.sb_ignition_status != null) {
    const ignitionOn =
      sensorData.sb_ignition_status.toString().toLowerCase() === 'true';
    const ONE_HOUR_AGO = Date.now() - 60 * 60 * 1000;
    const latestTs = Number(
      sensorData.latest_ts ?? sensorData.sb_ignition_status_ts ?? 0
    );
    return ignitionOn && latestTs > ONE_HOUR_AGO;
  }

  // Fallback: the original polled logic — device must be 'Online' and the poll
  // data recent (daysBehind ≤ 0.042 ≈ 1 hour).
  const deviceStatus = asset?.device?.deviceStatus;
  if (deviceStatus !== 'Online') {
    return false;
  }

  const ignitionStatus =
    sensorData?.sb_ignition_status?.toString().toLowerCase() === 'true';
  const isRecentData = (asset?.device?.daysBehind ?? Infinity) <= 0.042;

  return ignitionStatus && isRecentData;
};

export const isVehcleParked = (
  latestSensorDetails: any,
  asset: Asset,
  customerCode: string
): boolean => {
  const assetDetails = latestSensorDetails[`${asset.device.deviceId}`];
  if (assetDetails?._status === 'True') {
    const parkingLot = coordsMap[`${customerCode}`]; // we do not have parkingLot coords
    if (
      // what logic do we use here?
      assetDetails.gps_latitude === parkingLot[0] &&
      assetDetails.gps_longitude === parkingLot[1]
    ) {
      return true;
    }
    return false;
  }

  return false;
};

export const iconCount = (asset: Asset, suggestions: any): number => {
  let count = 0;
  if (assetHasDTC(asset)) {
    count++;
  }
  if (assetHasSuggestions(asset, suggestions) > 0) {
    count++;
  }
  return count;
};

export const createGroupedOutliers = (
  suggestions: any[],
  unitSystem: string,
  preferredTimeDisplayMode: preferredTimeDisplayModeTypes,
  timezone: string
): Record<string, any[]> => {
  if (!suggestions || suggestions.length === 0) return {};

  const { outlierTripsBasedOnTime } = formatTripData(
    suggestions[0],
    unitSystem,
    preferredTimeDisplayMode,
    timezone
  );

  return outlierTripsBasedOnTime !== 'None'
    ? (outlierTripsBasedOnTime as string[]).reduce((acc: any, item: string) => {
        const [trip, asset] = item.split(' for ');
        const [assetId, tripDetails] = asset.split(' with trip ');
        const [, tripTime] = tripDetails.split('of ');

        if (!acc[assetId]) {
          acc[assetId] = [];
        }

        acc[assetId].push({ trip, assetId, tripDetails, tripTime });
        return acc;
      }, {})
    : {};
};

/**
 * This function provides a unified way to handle trip notification click/navigation for suggestions
 *
 * @param {Asset | string} assetOrBumperNumber
 * @param {any} trip
 * @param {Asset[]} [filteredAssets]
 */
export const handleTripClickForSuggestions = (
  assetOrBumperNumber: Asset | string,
  trip: any,
  filteredAssets?: Asset[],
  navigate?: (path: string, options?: any) => void
): void => {
  if (!navigate) {
    console.warn(
      'Navigation function not provided to handleTripClickForSuggestions'
    );
    return;
  }

  let assetVin: string | null = null;
  let bumperNumber: string = '';

  if (typeof assetOrBumperNumber === 'string') {
    bumperNumber = assetOrBumperNumber;
    if (filteredAssets) {
      const asset = filteredAssets.find((a) => a.bumperNumber === bumperNumber);
      assetVin = asset ? asset.assetVin : null;
    }
  } else {
    const asset = assetOrBumperNumber;
    assetVin = asset.assetVin;
    bumperNumber = asset.bumperNumber;
  }

  if (!assetVin) {
    console.warn('Asset VIN not found for trip click');
    return;
  }

  const date = String(trip?.trip).split('on')?.[1];
  const tripNumber = trip.trip.match(/Trip (\d+)/)?.[1];

  navigate(`/production/load-cycle-analysis/${assetVin}`, {
    state: {
      type: 'trip-details',
      value: 0,
      asset: bumperNumber,
      date,
      selectedTrip: tripNumber,
    },
  });
};

/**
 * Handles navigation to alert details when a user clicks on an alert notification for alerts and DTCs.
 *
 * @param {Asset} [asset]
 * @param {Asset} [selectedAsset]
 * @param {'notifications' | 'dtc'} [type='notifications']
 * @param {(path: string, options?: any) => void} [navigate]
 *
 */
export const handleAlertClick = (
  navigate: NavigateFunction,
  asset?: Asset,
  selectedAsset?: Asset,
  type: 'notifications' | 'dtc' = 'notifications'
): void => {
  if (!navigate) {
    console.warn('Navigation function not provided to handleAlertClick');
    return;
  }

  const dataToPass = {
    bumperNumber: asset?.bumperNumber ?? selectedAsset?.bumperNumber,
    isActive: asset?.isActive ?? selectedAsset?.isActive,
  };

  if (type === 'notifications') {
    navigate('/notifications', {
      state: {
        data: dataToPass,
      },
    });
  }

  if (type === 'dtc') {
    navigate('/diagnostic-trouble-codes', {
      state: {
        data: dataToPass,
      },
    });
  }
};
